import { useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Upload, ArrowRightLeft, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Button } from "../../components/ui/button";
import CardCollapse from "./CardCollapse";

/* Preview → Production (or any env ↔ env) data migration helper.
 * Download: dumps selected whitelisted collections of the current environment
 *           as a JSON file.
 * Upload:   accepts that JSON file and upserts the collections into the
 *           current environment (match by `id` field).
 */

const CONTENT_COLLECTIONS = [
  { key: "menu_items", label: "Menüler" },
  { key: "pages", label: "Sayfalar ve bloklar" },
  { key: "services", label: "Çalışmalar / Hizmetler" },
  { key: "news", label: "Haberler" },
  { key: "news_categories", label: "Haber kategorileri" },
  { key: "trainings", label: "Eğitimler" },
  { key: "press", label: "Basın" },
  { key: "redirects", label: "Yönlendirmeler" },
];
const SETTINGS_KEY = "settings";

export default function MigrationTool() {
  const [picked, setPicked] = useState(() => Object.fromEntries(CONTENT_COLLECTIONS.map((c) => [c.key, true])));
  const [includeSettings, setIncludeSettings] = useState(false);
  const [mode, setMode] = useState("upsert"); // upsert | replace
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const fileRef = useRef(null);

  const toggle = (k) => setPicked((p) => ({ ...p, [k]: !p[k] }));

  const selectedCollections = () => {
    const base = CONTENT_COLLECTIONS.filter((c) => picked[c.key]).map((c) => c.key);
    return includeSettings ? [...base, SETTINGS_KEY] : base;
  };

  const download = async () => {
    const cols = selectedCollections();
    if (!cols.length) { toast.error("En az bir koleksiyon seçin"); return; }
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/backup/migration/export?collections=${encodeURIComponent(cols.join(","))}`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const ts = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-");
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `olgu-veri_${ts}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
      const counts = data?.counts || {};
      const total = Object.values(counts).reduce((s, n) => s + Number(n || 0), 0);
      toast.success(`İndirildi · ${total} kayıt (${Object.keys(counts).length} koleksiyon)`);
    } catch (e) {
      toast.error(errMsg(e) || "İndirme başarısız");
    } finally { setBusy(false); }
  };

  const upload = async (ev) => {
    const file = ev.target.files?.[0];
    ev.target.value = "";
    if (!file) return;
    if (!/\.json$/i.test(file.name)) { toast.error("Lütfen .json dosyası seçin"); return; }
    if (mode === "replace" && !window.confirm("DİKKAT: 'Değiştir' modu, seçili koleksiyonlardaki MEVCUT verileri siler. Devam edilsin mi?")) return;
    setBusy(true);
    setLastResult(null);
    try {
      const text = await file.text();
      let parsed;
      try { parsed = JSON.parse(text); } catch { throw new Error("Dosya geçerli JSON değil"); }
      const cols = parsed.collections || {};
      if (!cols || typeof cols !== "object") throw new Error("JSON içinde 'collections' alanı bulunamadı");
      // Server-side whitelists collections; here we send everything the file has.
      const payload = { schema_version: Number(parsed.schema_version || 1), collections: cols, mode };
      const { data } = await api.post("/admin/backup/migration/import", payload);
      setLastResult(data);
      const totalUp = Object.values(data.result || {}).reduce((s, r) => s + (r?.upserted || 0) + (r?.updated || 0), 0);
      toast.success(`İçe aktarıldı · ${totalUp} kayıt yazıldı`);
    } catch (e) {
      toast.error(e?.message || errMsg(e) || "İçe aktarım başarısız");
    } finally { setBusy(false); }
  };

  const anyPicked = Object.values(picked).some(Boolean) || includeSettings;

  return (
    <CardCollapse
      testid="migration-tool"
      icon={<ArrowRightLeft size={16} />}
      title="Veri Aktarımı (Preview ⇄ Production)"
      description="Bu ortamın içeriğini JSON olarak indirin, başka ortama (örn. canlı site) yükleyin."
    >
      <div className="border rounded-lg p-4 space-y-3 bg-stone-50/50">
        <p className="text-sm font-medium">1. Aktarılacak koleksiyonlar</p>
        <div className="grid sm:grid-cols-2 gap-2">
          {CONTENT_COLLECTIONS.map((c) => (
            <label key={c.key} className="flex items-center gap-2 text-sm cursor-pointer select-none" data-testid={`migration-col-${c.key}`}>
              <input type="checkbox" checked={!!picked[c.key]} onChange={() => toggle(c.key)} className="h-4 w-4" />
              <span>{c.label}</span>
              <code className="text-[10px] text-stone-400 ml-auto">{c.key}</code>
            </label>
          ))}
        </div>
        <label className="flex items-start gap-2 text-sm cursor-pointer select-none mt-2 border-t pt-3" data-testid="migration-col-settings">
          <input type="checkbox" checked={includeSettings} onChange={() => setIncludeSettings((v) => !v)} className="h-4 w-4 mt-0.5" />
          <span>
            <span className="font-medium">Ayarlar</span> <code className="text-[10px] text-stone-400">settings</code>
            <span className="block text-xs text-amber-700 mt-0.5">
              <AlertCircle size={11} className="inline mr-1" />
              Dikkat: hedef ortamın Resend, Telegram, reCAPTCHA ve diğer anahtarlarını üzerine yazar.
            </span>
          </span>
        </label>
      </div>

      <div className="border rounded-lg p-4 space-y-3">
        <p className="text-sm font-medium">2. Dosyayı indir (bu ortamdan)</p>
        <p className="text-xs text-stone-500">
          Butonu tıkladığınızda seçili koleksiyonlar bir .json dosyası olarak bilgisayarınıza iner.
          Dosyayı başka ortamın admin panelinde "Yükle" ile içe aktarabilirsiniz.
        </p>
        <Button type="button" onClick={download} disabled={!anyPicked || busy} data-testid="migration-download">
          {busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Download size={14} className="mr-1" />}
          JSON indir
        </Button>
      </div>

      <div className="border rounded-lg p-4 space-y-3">
        <p className="text-sm font-medium">3. Dosyayı yükle (bu ortama)</p>
        <div className="flex items-center gap-4 flex-wrap text-sm">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" name="mig-mode" value="upsert" checked={mode === "upsert"} onChange={() => setMode("upsert")} data-testid="migration-mode-upsert" />
            <span>Birleştir (upsert) <span className="text-xs text-stone-500">— aynı id güncellenir, eksikler eklenir</span></span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="radio" name="mig-mode" value="replace" checked={mode === "replace"} onChange={() => setMode("replace")} data-testid="migration-mode-replace" />
            <span>Değiştir (replace) <span className="text-xs text-red-600">— seçili koleksiyonlar önce silinir</span></span>
          </label>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept="application/json,.json" onChange={upload} className="hidden" data-testid="migration-upload-input" />
          <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="migration-upload-btn">
            {busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Upload size={14} className="mr-1" />}
            JSON yükle
          </Button>
        </div>
      </div>

      {lastResult?.result && (
        <div className="border rounded-lg p-4 bg-emerald-50/40" data-testid="migration-result">
          <p className="text-sm font-medium flex items-center gap-2 mb-2">
            <CheckCircle2 size={15} className="text-emerald-600" />
            Son içe aktarım sonucu <span className="text-xs text-stone-500">({lastResult.mode})</span>
          </p>
          <div className="text-xs grid sm:grid-cols-2 gap-1.5">
            {Object.entries(lastResult.result).map(([k, r]) => (
              <div key={k} className="flex items-center justify-between bg-white border rounded px-2 py-1">
                <code className="text-stone-500">{k}</code>
                <span>
                  <span className="text-emerald-700 font-semibold">{(r?.upserted || 0) + (r?.updated || 0)}</span>
                  <span className="text-stone-400"> / {r?.received || 0}</span>
                  {r?.skipped ? <span className="text-amber-600 ml-1">· {r.skipped} atlandı</span> : null}
                </span>
              </div>
            ))}
          </div>
          {lastResult.target_db && <p className="text-[11px] text-stone-500 mt-2">Hedef DB: <code>{lastResult.target_db}</code></p>}
        </div>
      )}
    </CardCollapse>
  );
}
